import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Search, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface LushaDiscoverButtonProps {
  entityType: 'candidate' | 'company_people';
  entityId: string;
  firstName: string;
  lastName: string;
  companyName: string;
  companyDomain?: string;
  linkedinUrl?: string;
  city?: string;
  email?: string;
  onSuccess?: (emails: string[], phones: string[]) => void;
  disabled?: boolean;
  size?: 'sm' | 'default' | 'lg' | 'icon';
  variant?: 'default' | 'outline' | 'ghost' | 'secondary';
}

export function LushaDiscoverButton({
  entityType,
  entityId,
  firstName,
  lastName,
  companyName,
  companyDomain,
  linkedinUrl,
  city,
  email,
  onSuccess,
  disabled = false,
  size = 'sm',
  variant = 'outline',
}: LushaDiscoverButtonProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<'success' | 'not_found' | 'error' | null>(null);

  const handleDiscover = async () => {
    if (!firstName || !lastName) {
      toast({
        title: 'Missing Information',
        description: 'First name and last name are required for contact discovery.',
        variant: 'destructive',
      });
      return;
    }

    if (!companyName) {
      toast({
        title: 'Missing Information',
        description: 'Company name is required for contact discovery.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke('lusha-lookup', {
        body: {
          firstName,
          lastName,
          companyName,
          companyDomain,
          linkedinUrl,
          city,
          email,
          entityType,
          entityId,
        },
      });

      if (error) throw error;

      if (data.success) {
        setResult('success');
        toast({
          title: 'Contact Discovered',
          description: `Found ${data.emails?.length || 0} email(s) and ${data.phones?.length || 0} phone(s).`,
        });
        onSuccess?.(data.emails || [], data.phones || []);
      } else if (data.status === 'not_found') {
        setResult('not_found');
        toast({
          title: 'No Results',
          description: 'No contact information found for this person.',
          variant: 'destructive',
        });
      } else if (data.error?.includes('rate limit')) {
        setResult('error');
        // Extract reset time if available
        const resetMatch = data.error.match(/Reset in (\d+) seconds/);
        const resetMinutes = resetMatch ? Math.ceil(parseInt(resetMatch[1]) / 60) : null;
        toast({
          title: 'Rate Limit Exceeded',
          description: resetMinutes 
            ? `Lusha API limit reached. Try again in ~${resetMinutes} minutes.`
            : 'Lusha API limit reached. Please try again later.',
          variant: 'destructive',
        });
      } else {
        setResult('error');
        toast({
          title: 'Discovery Failed',
          description: data.error || 'Failed to discover contact information.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Lusha lookup error:', error);
      setResult('error');
      toast({
        title: 'Error',
        description: error.message || 'Failed to discover contact information.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getIcon = () => {
    if (isLoading) return <Loader2 className="h-4 w-4 animate-spin" />;
    if (result === 'success') return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
    if (result === 'error' || result === 'not_found') return <XCircle className="h-4 w-4 text-destructive" />;
    return <Search className="h-4 w-4" />;
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant={variant}
          size={size}
          onClick={handleDiscover}
          disabled={disabled || isLoading || !firstName || !lastName || !companyName}
        >
          {getIcon()}
          {size !== 'icon' && (
            <span className="ml-1">
              {isLoading ? 'Discovering...' : 'Discover Contact'}
            </span>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p>Use Lusha to discover email and phone for {firstName} {lastName}</p>
      </TooltipContent>
    </Tooltip>
  );
}
